/**
 * Marketing banner list — priority-ordered configuration rows.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, type Column } from '@/components/common/Table';
import HasPermission from '@/components/common/HasPermission';
import { useHeaderActions } from '@/app/contexts/HeaderActionsContext';
import { marketingBannerService } from '@/app/services/marketingBannerService';
import type { MarketingBannerRecord } from '@/app/types/marketingBanners';
import { logger } from '@/app/utils/logger';

import './MarketingBannerList.scss';

export const MarketingBannerList: React.FC = () => {
  const navigate = useNavigate();
  const { setActions } = useHeaderActions();
  const [banners, setBanners] = useState<MarketingBannerRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadBanners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await marketingBannerService.queryBanners({ limit: 200, offset: 0 });
      setBanners(response.items);
    } catch (err) {
      logger.error('Failed to load marketing banners', { error: err });
      setError('Failed to load marketing banners.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(
    async (banner: MarketingBannerRecord) => {
      if (
        !window.confirm(
          `Delete marketing banner "${banner.name}" (priority ${banner.priority})? This cannot be undone.`,
        )
      ) {
        return;
      }
      try {
        const deleted = await marketingBannerService.deleteBanner(banner.id);
        if (deleted) {
          await loadBanners();
        } else {
          setError('Marketing banner not found.');
        }
      } catch (err) {
        logger.error('Failed to delete marketing banner', { error: err, id: banner.id });
        setError('Failed to delete marketing banner.');
      }
    },
    [loadBanners],
  );

  useEffect(() => {
    void loadBanners();
  }, [loadBanners]);

  useEffect(() => {
    setActions(
      <>
        <button className="btn-refresh" type="button" onClick={() => void loadBanners()} disabled={loading}>
          ↻ Refresh
        </button>
        <HasPermission permission="marketing_banner:write">
          <button className="btn-create" type="button" onClick={() => navigate('/marketing-banners/new')}>
            + New Banner
          </button>
        </HasPermission>
      </>,
    );
    return () => setActions(null);
  }, [setActions, loadBanners, loading, navigate]);

  const columns: Column<MarketingBannerRecord>[] = [
    { key: 'priority', header: 'Priority', render: (row) => row.priority },
    { key: 'name', header: 'Name', render: (row) => row.name },
    { key: 'status', header: 'Status', render: (row) => row.status },
    { key: 'source_type', header: 'Source', render: (row) => row.source_type },
    {
      key: 'title',
      header: 'Title',
      render: (row) => row.static_content?.title ?? '—',
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="marketing-banner-list__actions">
          <button
            type="button"
            className="marketing-banner-list__edit-link"
            onClick={() => navigate(`/marketing-banners/${row.id}/edit`)}
          >
            Edit
          </button>
          <HasPermission permission="marketing_banner:write">
            <button
              type="button"
              className="marketing-banner-list__delete-link"
              onClick={() => void handleDelete(row)}
            >
              Delete
            </button>
          </HasPermission>
        </div>
      ),
    },
  ];

  return (
    <div className="marketing-banner-list">
      {error && (
        <div className="marketing-banner-list__error" role="alert">
          {error}
        </div>
      )}
      {loading ? (
        <p className="marketing-banner-list__loading">Loading marketing banners…</p>
      ) : (
        <Table
          columns={columns}
          data={banners}
          keyExtractor={(row) => row.id}
          emptyMessage="No marketing banners configured."
        />
      )}
    </div>
  );
};

export default MarketingBannerList;
